/* eslint-disable class-methods-use-this */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MessageService } from '@arc/aureus';
import { TranslateService } from '@ngx-translate/core';
import { UserNotificationAction } from '@arc/arc-shared-frontend';
import {
  AppliedDiscountMeta,
  CreditAppliedActionPayload,
  DiscountExclusionReason,
  DiscountsHelperService,
  GetSessionSummaryResponse,
  LoadingSpinnerService,
  PayablePaymentStatus,
  PaymentDocumentHelperService,
  PaymentPreviewPayableV1Response,
  PaymentPreviewPaymentV1Response,
  PaymentPreviewV1Response,
  PaymentRulesResponseV1,
  PayOtherAmountV1Response,
  roundTo2Decimals,
  SessionDocumentInfo,
  SessionServiceFactory,
  SessionSummaryActionPayload,
  SessionTransactionService,
  UpdatedSessionDocuments,
  UpdatePayableDocument,
  UpdatePayableSessionRequestV1,
  UpdateSelectedDocumentInfoActionSuccessPayload,
  UpdateSessionPayable,
  UpdateTransactionStatusActionPayload,
} from '@arc/unified-payments-frontend/shared-payment';
import { SessionActions } from '../actions';
import {
  GetSessionSummaryErrorAction,
  GetSessionSummarySuccessAction,
  UpdateModifiedDocumentsUrnListAction,
  UpdateRUMSessionTagsAction,
} from '../actions/session.actions';
import {
  selectCreditMemoSubtotal,
  selectIsBuyerOriginApp,
  selectSessionPaymentProcess,
  selectSubTotal,
} from '../selectors/session.selectors';
import {
  PaymentRuleStore,
  selectBlockDuplicatePaymentsEnabled,
} from '../../payment-rules';
import {
  SelectedAppliedDiscountMetaStore,
  SessionStore,
} from '../reducers/session.reducer';
import {
  BtPayPaymentInformationActions,
  PaymentPreviewStore,
} from '../../btpay';
import { BaseEffect } from '../../helpers/base-effect';
import {
  selectAreThereDocumentsPendingDeselection,
  selectPayableRestrictions,
  selectSelectedDocuments,
  selectSortedSessionDocuments,
} from '../selectors';
import { selectTenantId } from '../../external-selectors';
import { concatLatestFrom } from '@ngrx/operators';
import {
  HostedPaymentsPadResultComponentSessionActions,
  PoaPadResultComponentSessionActions,
  PoiMakePaymentComponentActions,
  PoiPadResultComponentSessionActions,
  PoiSelectPaymentMethodComponentActions,
  PayOnInvoiceSelectPaymentMethodActions,
} from '../../component-actions';

@Injectable()
export class SessionEffects extends BaseEffect {
  constructor(
    private store: Store,
    private sessionServiceFactory: SessionServiceFactory,
    private paymentDocumentService: PaymentDocumentHelperService,
    private discountService: DiscountsHelperService,
    private messageService: MessageService,
    private translationService: TranslateService,
    private sessionTransactionService: SessionTransactionService,
    actions$: Actions,
    loadingSpinnerService: LoadingSpinnerService,
  ) {
    super(actions$, loadingSpinnerService);
  }

  getSessionSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.GetSessionSummaryAction),
      map((action) => action.payload),
      concatLatestFrom(() => [this.store.select(selectTenantId)]),
      exhaustMap(
        ([payload, tenantId]: [SessionSummaryActionPayload, string]) => {
          return this.sessionServiceFactory
            .create()
            .getSessionSummaryResponseV1(tenantId, payload.sessionId)
            .pipe(
              map((response: GetSessionSummaryResponse) => {
                const session = this.syncSessionWithPayment(response);
                return GetSessionSummarySuccessAction({
                  payload: session,
                });
              }),
              catchError((error: HttpErrorResponse) =>
                of(GetSessionSummaryErrorAction({ payload: error })),
              ),
            );
        },
      ),
    ),
  );

  updateRUMSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.GetSessionSummarySuccessAction),
      map((action) => {
        return UpdateRUMSessionTagsAction({ payload: action.payload });
      }),
    ),
  );

  updateRUMSessionOnRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PoaPadResultComponentSessionActions.loadSessionRedirectStateSuccess,
        PoiPadResultComponentSessionActions.loadSessionRedirectStateSuccess,
        HostedPaymentsPadResultComponentSessionActions.loadSessionRedirectStateSuccess,
      ),
      map((action) => {
        return UpdateRUMSessionTagsAction({
          payload: action.payload.redirectState.sessionSummary,
        });
      }),
    ),
  );

  /** istanbul ignore next */
  loadUntilSessionSummaryCompletes$ = createEffect(
    () =>
      this.loadUntilActionsComplete(
        [SessionActions.GetSessionSummaryAction.type],
        [
          SessionActions.GetSessionSummarySuccessAction.type,
          SessionActions.GetSessionSummaryErrorAction.type,
        ],
      ),
    { dispatch: false },
  );

  updateTransactionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.UpdateTransactionStatus),
      map((action) => action.payload),
      concatLatestFrom(() => [this.store.select(selectTenantId)]),
      exhaustMap(([payload, tenantId]) =>
        this.updateTransactionStatus(tenantId, payload),
      ),
    ),
  );

  updatePayOtherSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.UpdatePayOtherPayablesAction),
      withLatestFrom(
        this.store.select(SessionStore),
        this.store.select(selectTenantId),
      ),
      exhaustMap(([action, sessionSummary, tenantId]) => {
        const request = this.BuildUpdateSessionPayablesRequest(action.payload);
        return this.sessionServiceFactory
          .create()
          .UpdateSessionPayablesSessionV1(
            tenantId,
            sessionSummary.sessionId,
            request,
          )
          .pipe(
            map(() => {
              return SessionActions.GetSessionSummaryAction({
                payload: {
                  sessionId: sessionSummary.sessionId,
                },
              });
            }),
            catchError((error: HttpErrorResponse) =>
              of(SessionActions.UpdatePayOtherPayblesErrorAction({ error })),
            ),
          );
      }),
    ),
  );

  /* istanbul ignore next */
  loadUntilTransactionStatusUpdated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.UpdateTransactionStatus),
        exhaustMap(() => {
          this.loadUntilActionsComplete(
            [SessionActions.UpdateTransactionStatus.type],
            [
              SessionActions.UpdateTransactionStatusSuccess.type,
              SessionActions.UpdateTransactionStatusError.type,
            ],
          );
          return of();
        }),
      ),
    { dispatch: false },
  );

  // deprecated in favor of component effects
  intlCalculateZeroDollarPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SessionActions.CalculateZeroDollarPayment,
        SessionActions.UpdateSelectedSessionDocumentsActionSuccess,
        SessionActions.IntlUpdateAppliedDiscountAmountAction,
      ),
      withLatestFrom(
        this.store.select(selectSessionPaymentProcess),
        this.store.select(selectSelectedDocuments),
        this.store.select(selectSubTotal),
        this.store.select(selectCreditMemoSubtotal),
        this.store.select(PaymentRuleStore),
        this.store.select(PaymentPreviewStore),
      ),
      filter(([, paymentProcess]) => paymentProcess !== 'Internal'),
      exhaustMap(
        ([
          ,
          ,
          selectedDocuments,
          subTotal,
          creditMemoSubtotal,
          paymentRules,
          paymentPreview,
        ]) => {
          const payload =
            paymentPreview?.payments.length > 1
              ? this.calculateZeroDollarPaymentsUsingCreditMemos(
                  selectedDocuments,
                  paymentPreview,
                  paymentRules.settings?.generalPaymentSettings
                    .zeroDollarPaymentsEnabled,
                )
              : this.calculateZeroDollarPaymentUsingCreditMemos(
                  creditMemoSubtotal,
                  subTotal,
                  selectedDocuments,
                  paymentRules.settings?.generalPaymentSettings
                    .zeroDollarPaymentsEnabled,
                );
          return of(
            SessionActions.IntlZeroDollarPaymentCalculated({ payload }),
          );
        },
      ),
    ),
  );

  applySoftBlockDocumentDeselection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.IntlZeroDollarPaymentCalculated),
      concatLatestFrom(() => [
        this.store.select(selectAreThereDocumentsPendingDeselection),
      ]),
      filter(
        ([, areThereDocumentsPendingDeselection]) =>
          areThereDocumentsPendingDeselection,
      ),
      map(() => SessionActions.applySoftBlockDocumentDeselection()),
    ),
  );

  updateSelectedDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BtPayPaymentInformationActions.CreatePaymentPreviewCompleteAction),
      withLatestFrom(
        this.store.select(selectSortedSessionDocuments),
        this.store.select(PaymentPreviewStore),
        this.store.select(selectBlockDuplicatePaymentsEnabled),
        this.store.select(PaymentRuleStore),
        this.store.select(selectPayableRestrictions),
        this.store.select(SessionStore),
      ),
      exhaustMap(
        ([
          ,
          selectedDocuments,
          paymentPreview,
          isHardBlock,
          paymentRules,
          existingRestrictions,
          sessionSummary,
        ]) => {
          const allowOverPay =
            paymentRules?.settings?.shortAndOverPaySettings?.allowOverPay ??
            false;

          // Fallback for documents excluded from the payment preview response
          // (e.g. Statement documents when allowStatementPayments is off) so
          // every selected document still gets its owning buyer account.
          const buyerAccountByUrn = new Map<string, string>();
          sessionSummary?.payables?.forEach((payable) => {
            payable.documents?.forEach((document) => {
              if (payable.accountInfo?.number) {
                buyerAccountByUrn.set(document.urn, payable.accountInfo.number);
              }
            });
          });

          const processedUrns = new Set<string>();

          paymentPreview.payments.forEach((payment) => {
            const payables = paymentPreview.payments.flatMap(
              (p) => p.payment.payables,
            );
            const buyerAccount = payment.account.buyerAccountIdentifier;
            const payableBurns = payment.payment.payables.map((p) => p.burn);
            const documents = selectedDocuments.filter((document) =>
              payableBurns.includes(document.urn),
            );

            for (let d = 0; d < documents.length; d += 1) {
              documents[d].buyerAccount = buyerAccount.replace(
                'accountNumber:',
                '',
              );

              documents[d].duplicatePaymentSoftBlockPending = false;

              const payable = payables.find((p) => p.burn === documents[d].urn);
              processedUrns.add(documents[d].urn);

              if (payable && !documents[d].isCreditMemo) {
                const isPaidInFull =
                  payable.hasPastPayments === PayablePaymentStatus.FullyPaid ||
                  payable.hasPastPayments ===
                    PayablePaymentStatus.FullyConsumed;

                if (isPaidInFull && (!allowOverPay || isHardBlock)) {
                  documents[d].duplicatePaymentSoftBlockPending = true;
                }
              }
            }
          });

          selectedDocuments.forEach((doc) => {
            if (!doc.buyerAccount) {
              const fallbackBuyerAccount = buyerAccountByUrn.get(doc.urn);
              if (fallbackBuyerAccount) {
                doc.buyerAccount = fallbackBuyerAccount;
              }
            }

            if (!processedUrns.has(doc.urn) && !doc.isCreditMemo) {
              const hadDuplicatePaymentWarning = existingRestrictions?.some(
                (r) =>
                  r.documentUrn === doc.urn &&
                  (r.hints?.includes(
                    'ARC_PAYMENT_DUPLICATE_PAYMENT_TOOLTIP_WARNING_SOFT',
                  ) ||
                    r.hints?.includes(
                      'ARC_PAYMENT_DUPLICATE_PAYMENT_TOOLTIP_WARNING_HARD',
                    )),
              );

              if (hadDuplicatePaymentWarning) {
                doc.duplicatePaymentSoftBlockPending = true;
              }
            }
          });

          const payload: UpdatedSessionDocuments = {
            updatedSessionDocuments: [...selectedDocuments],
          };

          return of(
            SessionActions.UpdateSelectedDocumentsComplete({
              payload,
            }),
          );
        },
      ),
    ),
  );

  displayNegativePaymentNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.ExecuteNegativePaymentAction),
      exhaustMap(() => {
        return of(
          new UserNotificationAction({
            type: 'error',
            message: this.translationService.instant(
              'ARC_PAYMENT_ZERO_DOLLAR_ON_EDIT_PAYABLE_ERROR',
            ) as string,
          }),
        );
      }),
    ),
  );

  displayCreditMemosAppliedNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SessionActions.IntlZeroDollarPaymentCalculated,
          PoiMakePaymentComponentActions.creditPreApplied,
          PoiMakePaymentComponentActions.creditApplied,
          PoiSelectPaymentMethodComponentActions.creditApplied,
        ),
        filter((action) => action.payload != null),
        withLatestFrom(
          this.store.select(PaymentRuleStore),
          this.store.select(PaymentPreviewStore),
        ),
        exhaustMap(([, paymentRules, paymentPreview]) => {
          this.openCreditMemoNotification(
            paymentPreview?.payments.length > 1,
            paymentRules.settings?.generalPaymentSettings
              ?.zeroDollarPaymentsEnabled,
          );
          return of();
        }),
      ),
    { dispatch: false },
  );

  displayEditPayableAmountNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.PayableAmountEditedErrorAction),
        withLatestFrom(
          this.store.select(PaymentRuleStore),
          this.store.select(PaymentPreviewStore),
        ),
        exhaustMap(([, paymentRules, paymentPreview]) => {
          this.openEditPayableAmountError(
            paymentPreview?.payments.length > 1,
            paymentRules.settings?.generalPaymentSettings
              ?.zeroDollarPaymentsEnabled,
          );
          return of();
        }),
      ),
    { dispatch: false },
  );

  displayEditPayableAmountShortPaymentNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.PayableAmountEditedShortPaymentErrorAction.type),
      exhaustMap(() =>
        of(
          new UserNotificationAction({
            type: 'error',
            message: this.translationService.instant(
              'ARC_PAYMENT_EDIT_PAYABLE_SHORT_PAYMENT_ERROR',
            ) as string,
          }),
        ),
      ),
    ),
  );

  displayEditPayableAmountOverPaymentNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.PayableAmountEditedOverPaymentErrorAction.type),
      exhaustMap(() =>
        of(
          new UserNotificationAction({
            type: 'error',
            message: this.translationService.instant(
              'ARC_PAYMENT_EDIT_PAYABLE_OVER_PAYMENT_ERROR',
            ) as string,
          }),
        ),
      ),
    ),
  );

  displayEditPayableAmountCreditUsedForOverPaymentNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SessionActions.PayableAmountEditedCreditUsedForOverPaymentErrorAction
            .type,
        ),
        exhaustMap(() =>
          of(
            new UserNotificationAction({
              type: 'error',
              message: this.translationService.instant(
                'ARC_PAYMENT_EDIT_PAYABLE_CREDIT_USED_FOR_OVER_PAY_ERROR',
              ) as string,
            }),
          ),
        ),
      ),
  );

  validateCreditMemoPayableAmountEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.validateCreditMemoPayableAmountEdit),
      withLatestFrom(
        this.store.select(selectSelectedDocuments),
        this.store.select(selectSubTotal),
        this.store.select(selectCreditMemoSubtotal),
        this.store.select(PaymentRuleStore),
        this.store.select(PaymentPreviewStore),
      ),
      exhaustMap(
        ([
          action,
          selectedDocuments,
          subTotal,
          creditMemoSubTotal,
          paymentRules,
          paymentPreview,
        ]) => {
          const editedDocument = action.payload.updatedSelectedDocument;
          let splitDocuments: SessionDocumentInfo[];
          let splitSubTotal: number;
          let splitCreditTotal: number;

          if (paymentPreview?.payments.length > 1) {
            splitDocuments = selectedDocuments.filter((document) =>
              paymentPreview.payments
                .find((p) =>
                  p.payment.payables.some((d) => d.burn === editedDocument.urn),
                )
                .payment.payables.map((p) => p.burn)
                .includes(document.urn),
            );
            splitSubTotal =
              this.paymentDocumentService.getSubTotal(splitDocuments);
            splitCreditTotal =
              this.paymentDocumentService.getCreditMemoSubTotal(splitDocuments);
          } else {
            splitDocuments = selectedDocuments;
            splitSubTotal = subTotal;
            splitCreditTotal = creditMemoSubTotal;
          }

          const errors = this.validateCreditMemoPayableAmount(
            editedDocument,
            splitDocuments,
            splitSubTotal,
            splitCreditTotal,
            paymentRules.settings.generalPaymentSettings
              ?.zeroDollarPaymentsEnabled,
          );

          if (errors.isNegativePayment) {
            return of(SessionActions.PayableAmountEditedErrorAction());
          }
          if (errors.isZeroDollarPayment) {
            return of(SessionActions.PayableAmountEditedErrorAction());
          }
          if (errors.isCreditUsedForOverPayment) {
            return of(
              SessionActions.PayableAmountEditedCreditUsedForOverPaymentErrorAction(),
            );
          }
          return of(
            SessionActions.UpdateSelectedSessionDocumentsAction({
              payload: action.payload,
            }),
          );
        },
      ),
    ),
  );

  validateInvoicePayableAmountEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.ValidateInvoicePayableAmountEditAction),
      withLatestFrom(
        this.store.select(selectSelectedDocuments),
        this.store.select(selectSubTotal),
        this.store.select(PaymentRuleStore),
        this.store.select(PaymentPreviewStore),
        this.store.select(SelectedAppliedDiscountMetaStore),
      ),
      exhaustMap(
        ([
          action,
          selectedDocuments,
          subTotal,
          paymentRules,
          paymentPreview,
          discountMeta,
        ]) => {
          const editedDocument = action.payload.updatedSelectedDocument;
          let splitDocuments: SessionDocumentInfo[];
          let splitSubTotal: number;

          if (paymentPreview?.payments.length > 1) {
            splitDocuments = selectedDocuments.filter((document) =>
              paymentPreview.payments
                .find((p) =>
                  p.payment.payables.some((d) => d.burn === editedDocument.urn),
                )
                .payment.payables.map((p) => p.burn)
                .includes(document.urn),
            );
            splitSubTotal =
              this.paymentDocumentService.getSubTotal(splitDocuments);
          } else {
            splitDocuments = selectedDocuments;
            splitSubTotal = subTotal;
          }

          const errors = this.validateInvoicePayableAmountEdit(
            editedDocument,
            splitDocuments,
            splitSubTotal,
            paymentRules,
            discountMeta,
          );

          if (errors.isNegativePayment) {
            return of(SessionActions.PayableAmountEditedErrorAction());
          }
          if (errors.isZeroDollarPayment) {
            return of(SessionActions.PayableAmountEditedErrorAction());
          }
          if (errors.isShortPayment) {
            return of(
              SessionActions.PayableAmountEditedShortPaymentErrorAction(),
            );
          }
          if (errors.isOverPayment) {
            return of(
              SessionActions.PayableAmountEditedOverPaymentErrorAction(),
            );
          }
          return of(
            SessionActions.UpdateSelectedSessionDocumentsAction({
              payload: action.payload,
            }),
          );
        },
      ),
    ),
  );

  updateSelectedSessionDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.UpdateSelectedSessionDocumentsAction),
      concatLatestFrom(() => [
        this.store.select(selectSortedSessionDocuments),
        this.store.select(SelectedAppliedDiscountMetaStore),
      ]),
      exhaustMap(([action, selectedDocuments, discountMeta]) => {
        const payload = this.updateSelectedSessionDocuments(
          action.payload.updatedSelectedDocument,
          selectedDocuments,
          discountMeta?.find(
            (d) => d.urn === action.payload.updatedSelectedDocument.urn,
          ),
        );

        return of(
          SessionActions.UpdateSelectedSessionDocumentsActionSuccess({
            payload,
          }),
        );
      }),
    ),
  );

  intlApplyDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.CreatePaymentPreviewCompleteAction.type,
        BtPayPaymentInformationActions.UpdatePaymentPreviewCompleteAction.type,
      ),
      concatLatestFrom(() => [
        this.store.select(selectSessionPaymentProcess),
        this.store.select(selectSelectedDocuments),
        this.store.select(PaymentPreviewStore),
      ]),
      filter(([, paymentProcess]) => paymentProcess !== 'Internal'),
      exhaustMap(([, , selectedDocuments, paymentPreview]) => {
        let docToBeUpdated: SessionDocumentInfo;
        const payload: AppliedDiscountMeta[] = [];
        const intlPayableFilter = (payable: PaymentPreviewPayableV1Response) =>
          !payable.discountExclusionReasons?.includes(
            DiscountExclusionReason.NotEnoughDiscountInfo.toString(),
          ) &&
          payable.availableDiscount !== 0 &&
          payable.discountExpirationDate &&
          new Date(payable.discountExpirationDate).getUTCFullYear() !== 1;

        paymentPreview.payments.forEach(
          (paymentPreviewResponse: PaymentPreviewPaymentV1Response) => {
            paymentPreviewResponse.payment.payables
              .filter(intlPayableFilter)
              .forEach((payableResponse: PaymentPreviewPayableV1Response) => {
                docToBeUpdated = selectedDocuments.find(
                  (document) => document.urn === payableResponse.burn,
                );
                const paymentAmountIncludesDiscount =
                  payableResponse.isDiscountApplied &&
                  docToBeUpdated.originalPayableAmount ===
                    roundTo2Decimals(
                      docToBeUpdated.openBalance -
                        payableResponse.appliedDiscount,
                    );

                docToBeUpdated.originalSessionPayableAmount =
                  paymentAmountIncludesDiscount
                    ? docToBeUpdated.openBalance
                    : docToBeUpdated.originalSessionPayableAmount;

                docToBeUpdated.originalPayableAmount =
                  payableResponse.isDiscountApplied
                    ? roundTo2Decimals(
                        docToBeUpdated.originalSessionPayableAmount -
                          payableResponse.appliedDiscount,
                      )
                    : docToBeUpdated.originalSessionPayableAmount;

                docToBeUpdated.payableAmount = payableResponse.amount;

                // get the discount state
                const discountState =
                  this.discountService.getPayableDiscountState(payableResponse);
                const result: AppliedDiscountMeta = {
                  urn: docToBeUpdated.urn,
                  appliedDiscountAmount: payableResponse.appliedDiscount ?? 0,
                  discountAmount: payableResponse.availableDiscount ?? 0,
                  discountDate: payableResponse.discountExpirationDate,
                  isDiscountApplied: payableResponse.isDiscountApplied,
                  discountState,
                  discountedPayableAmount: payableResponse.amount,
                };
                payload.push(result);
              });

            // Clear discountDate for payables excluded from discount processing
            // so stale sentinel dates (e.g. 0001-01-01) are not rendered in the UI.
            paymentPreviewResponse.payment.payables
              .filter((payable) => !intlPayableFilter(payable))
              .forEach((payable: PaymentPreviewPayableV1Response) => {
                const doc = selectedDocuments.find(
                  (document) => document.urn === payable.burn,
                );
                if (doc) {
                  doc.discountDate = null;
                }
              });
          },
        );
        return of(
          SessionActions.IntlUpdateAppliedDiscountAmountAction({
            payload,
          }),
        );
      }),
    ),
  );

  private updateTransactionStatus(
    tenantId: string,
    payload: UpdateTransactionStatusActionPayload,
  ) {
    return this.sessionTransactionService
      .updateTransactionStatus(tenantId, payload.sessionId, payload.status)
      .pipe(
        map((response) =>
          SessionActions.UpdateTransactionStatusSuccess({ response }),
        ),
        catchError((error: HttpErrorResponse) =>
          of(SessionActions.UpdateTransactionStatusError({ error })),
        ),
      );
  }

  private calculateZeroDollarPaymentsUsingCreditMemos(
    sessionDocInfo: SessionDocumentInfo[],
    paymentPreview: PaymentPreviewV1Response,
    isZeroDollarPaymentEnabled: boolean,
  ): CreditAppliedActionPayload {
    let calculated = false;
    paymentPreview.payments.forEach((payment) => {
      const payableBurns = payment.payment.payables.map((p) => p.burn);
      const documents = sessionDocInfo.filter((document) =>
        payableBurns.includes(document.urn),
      );
      const subTotal = this.paymentDocumentService.getSubTotal(documents);
      const creditTotal =
        this.paymentDocumentService.getCreditMemoSubTotal(documents);

      const result = this.calculateZeroDollarPaymentUsingCreditMemos(
        creditTotal,
        subTotal,
        documents,
        isZeroDollarPaymentEnabled,
      );

      if (result) {
        calculated = true;
      }
    });

    if (!calculated) {
      return null;
    }

    const actionResult: CreditAppliedActionPayload = {
      updatedSessionDocuments: [...sessionDocInfo],
    };

    return actionResult;
  }

  private calculateZeroDollarPaymentUsingCreditMemos(
    creditMemoSubTotal: number,
    subTotal: number,
    sessionDocInfo: SessionDocumentInfo[],
    isZeroDollarPaymentEnabled: boolean,
  ): CreditAppliedActionPayload {
    const sortedCreditMemos = sessionDocInfo.filter((d) => d.isCreditMemo);
    if (
      sortedCreditMemos.length === 0 ||
      Math.abs(creditMemoSubTotal) < subTotal
    ) {
      return null;
    }
    // if the total amount of credit memos is greater than or equal to the subtotal then we will begin applying the credit towards the the sub-total
    // open balance allocated starts at zero and is used to determine the threshold for when the subtotal is reached
    let openBalanceAllocated = 0;
    sortedCreditMemos.forEach((creditMemoDocData) => {
      // if the open balance that has been allocated thus far is still less than the subtotal then we need to apply more credit to being to equal the subtotal to make a zero dollar payment
      if (Math.abs(openBalanceAllocated) < subTotal) {
        // keep adding the open balance of the credit memos up until we hit the subtotal
        openBalanceAllocated += creditMemoDocData.openBalance;
        // if we have hit the subtotal then we need to either use all or a partial amount of the current open balance for the credit memo
        if (Math.abs(openBalanceAllocated) >= subTotal) {
          this.store.dispatch(
            UpdateModifiedDocumentsUrnListAction({
              payload: {
                urn: creditMemoDocData.urn,
                source: 'zeroDollarAdjustment',
              },
            }),
          );
          // find the difference (eg., subtotal = 5, openBalanceAllocated = -6 then 5 + (-6) = -1)
          const difference = subTotal + openBalanceAllocated;
          // now in this case we want to extract the partial amount to be used for this credit memos payable amount as in the above example the credit memo is greater than the subtotal
          // eg., payable amount = -6 + 1 = -5 (we only need to use -5 credits as the subtotal is 5)
          // eslint-disable-next-line no-param-reassign
          creditMemoDocData.payableAmount = +(
            creditMemoDocData.openBalance + Math.abs(difference)
          ).toFixed(2);
          // if zero dollar payments is off then we need to add one penny from the last credit memo processed so we have a Total Payment Amount of .01 cents (we do not want to zero out)
          if (!isZeroDollarPaymentEnabled) {
            // eslint-disable-next-line no-param-reassign
            creditMemoDocData.payableAmount = +(
              creditMemoDocData.payableAmount + 0.01
            ).toFixed(2);
          }
        }
      } else {
        this.store.dispatch(
          UpdateModifiedDocumentsUrnListAction({
            payload: {
              urn: creditMemoDocData.urn,
              source: 'zeroDollarAdjustment',
            },
          }),
        );

        // if we have reached the subtotal then we need to assign all payable amounts for remaining credit memos that went unused to zero
        // eslint-disable-next-line no-param-reassign
        creditMemoDocData.payableAmount = 0;
      }
    });

    const actionResult: CreditAppliedActionPayload = {
      // merge both credit memos and non-credit memos back together.
      updatedSessionDocuments: sessionDocInfo
        .filter((x) => !x.isCreditMemo)
        .concat(sortedCreditMemos),
    };

    return actionResult;
  }

  // eslint-disable-next-line class-methods-use-this
  private updateSelectedSessionDocuments(
    updatedSessionDocument: SessionDocumentInfo,
    selectedSessionDocuments: SessionDocumentInfo[],
    discountData: AppliedDiscountMeta,
  ): UpdateSelectedDocumentInfoActionSuccessPayload {
    updatedSessionDocument.overrideDiscount =
      discountData?.discountedPayableAmount != null &&
      updatedSessionDocument.payableAmount >
        discountData.discountedPayableAmount &&
      updatedSessionDocument.payableAmount <=
        updatedSessionDocument.originalSessionPayableAmount;

    const actionResult: UpdateSelectedDocumentInfoActionSuccessPayload = {
      updatedSelectedDocuments: selectedSessionDocuments
        .filter((x) => x.urn !== updatedSessionDocument.urn)
        .concat(updatedSessionDocument),
    };

    return actionResult;
  }

  private openCreditMemoNotification(
    hasDocumentsFromMultipleAccounts: boolean,
    isZeroDollarPaymentEnabled: boolean,
  ) {
    let errorKey: string;
    if (hasDocumentsFromMultipleAccounts) {
      errorKey = isZeroDollarPaymentEnabled
        ? 'ARC_PAYMENT_CREDIT_MEMO_AMOUNT_ADJUSTED_LINKED_ACCOUNTS'
        : 'ARC_PAYMENT_CREDIT_MEMO_AMOUNT_ADJUSTED_NON_ZERO_DOLLAR_LINKED_ACCOUNTS';
    } else {
      errorKey = isZeroDollarPaymentEnabled
        ? 'ARC_PAYMENT_CREDIT_MEMO_AMOUNT_ADJUSTED'
        : 'ARC_PAYMENT_CREDIT_MEMO_AMOUNT_ADJUSTED_NON_ZERO_DOLLAR';
    }

    this.messageService.dismissChannel('creditMemoNotification');
    this.messageService.open(
      this.translationService.instant(errorKey) as string,
      null,
      {
        channel: 'creditMemoNotification',
        severity: 'info',
        duration: 0,
        dismissOnNavigate: true,
        dismissOthers: false,
      },
    );
  }

  private openEditPayableAmountError(
    hasDocumentsFromMultipleAccounts: boolean,
    isZeroDollarPaymentEnabled: boolean,
  ) {
    let errorKey: string;
    if (hasDocumentsFromMultipleAccounts) {
      errorKey = isZeroDollarPaymentEnabled
        ? 'ARC_PAYMENT_ZERO_DOLLAR_ON_EDIT_PAYABLE_ERROR_LINKED_ACCOUNTS'
        : 'ARC_PAYMENT_ZERO_DOLLAR_OFF_EDIT_PAYABLE_ERROR_LINKED_ACCOUNTS';
    } else {
      errorKey = isZeroDollarPaymentEnabled
        ? 'ARC_PAYMENT_ZERO_DOLLAR_ON_EDIT_PAYABLE_ERROR'
        : 'ARC_PAYMENT_ZERO_DOLLAR_OFF_EDIT_PAYABLE_ERROR';
    }

    this.messageService.open(
      this.translationService.instant(errorKey) as string,
      null,
      {
        severity: 'danger',
        channel: 'payableAmountError',
        duration: 0,
        dismissOnNavigate: true,
        dismissOthers: true,
      },
    );
  }

  private validateCreditMemoPayableAmount = (
    editedDocument: SessionDocumentInfo,
    originalDocuments: SessionDocumentInfo[],
    subTotal: number,
    creditMemoSubTotal: number,
    isZeroDollarPaymentEnabled: boolean,
  ) => {
    // find original document payable amount being edited
    const originalPayableAmount: number = originalDocuments.find(
      (x) => x.urn === editedDocument.urn,
    ).payableAmount;

    const originalCreditMemoSubTotal: number = creditMemoSubTotal;
    const editedCreditMemoPayableAmount: number = editedDocument.payableAmount;
    const editedDifference: number = roundTo2Decimals(
      originalPayableAmount - editedCreditMemoPayableAmount,
    );
    const editedCreditMemoSubTotal: number = roundTo2Decimals(
      originalCreditMemoSubTotal - editedDifference,
    );

    // take the original subtotal (and surcharge fee) and minus out the edited credit memo subtotal and validate
    const editedTotalAmountToPay: number = roundTo2Decimals(
      subTotal + editedCreditMemoSubTotal,
    );

    const invoices = originalDocuments.filter((d) => !d.isCreditMemo);
    let originalInvoicePayableAmountTotal = 0;

    if (invoices.length > 0) {
      originalInvoicePayableAmountTotal = invoices
        .map((d) =>
          d.isFullInvoiceDispute ? d.payableAmount : d.originalPayableAmount,
        )
        .reduce((a, b) => a + b);
    }

    const errors = {
      isNegativePayment: editedTotalAmountToPay < 0,
      isZeroDollarPayment:
        !isZeroDollarPaymentEnabled && editedTotalAmountToPay === 0,
      isCreditUsedForOverPayment:
        Math.abs(editedCreditMemoSubTotal) > originalInvoicePayableAmountTotal,
    };

    return errors;
  };

  private validateInvoicePayableAmountEdit = (
    editedDocument: SessionDocumentInfo,
    selectedDocuments: SessionDocumentInfo[],
    subTotal: number,
    paymentRules: PaymentRulesResponseV1,
    discountMeta: AppliedDiscountMeta[],
  ) => {
    const allowZeroDollarPayments =
      paymentRules?.settings.generalPaymentSettings.zeroDollarPaymentsEnabled;
    const allowShortPay =
      paymentRules?.settings.shortAndOverPaySettings?.allowShortPay;
    const allowOverPay =
      paymentRules?.settings.shortAndOverPaySettings?.allowOverPay;

    const currentPayableAmount = selectedDocuments.find(
      (x) => x.invoiceNumber === editedDocument.invoiceNumber,
    ).payableAmount;

    const editedDifference = roundTo2Decimals(
      currentPayableAmount - editedDocument.payableAmount,
    );
    const editedSubTotal = roundTo2Decimals(subTotal - editedDifference);
    const discountedAmount = discountMeta?.find(
      (d) => d.urn === editedDocument.urn,
    )?.discountedPayableAmount;

    const errors = {
      isNegativePayment: editedSubTotal < 0,
      isZeroDollarPayment: !allowZeroDollarPayments && editedSubTotal === 0,
      isOverPayment:
        !allowOverPay &&
        !editedDocument.isFullInvoiceDispute &&
        editedDocument.payableAmount >
          editedDocument.originalSessionPayableAmount,
      isShortPayment:
        !allowShortPay &&
        editedDocument.payableAmount <
          (discountedAmount ?? editedDocument.originalPayableAmount),
    };

    return errors;
  };

  private syncSessionWithPayment(session: GetSessionSummaryResponse) {
    const syncedSession = session;
    if (session.payment) {
      syncedSession.payment = session.payment;

      if (session.payment.transactions) {
        syncedSession.payment.transactions = session.payment.transactions;
      }

      if (session.payment.receipts) {
        syncedSession.contacts = session.payment.receipts;
      }

      if (session.payment.transactions[0].payables) {
        const { payables } = session.payment.transactions[0];
        const { documents } = session.payables[0];

        documents.forEach((document) => {
          const payable = payables.filter((p) => p.urn === document.urn)[0];

          if (payable) {
            // eslint-disable-next-line no-param-reassign
            document.payableAmount = payable.transactionAmount;
          }
        });

        syncedSession.payables[0].documents = documents;
      }
    }

    return syncedSession;
  }

  private BuildUpdateSessionPayablesRequest = (
    response: PayOtherAmountV1Response,
  ): UpdatePayableSessionRequestV1 => {
    // eslint-disable-next-line prefer-const
    let buyerAccountIdentifier = '';
    const updateSessionPayable: UpdateSessionPayable[] = [];
    response.payables.forEach((payable) => {
      if (buyerAccountIdentifier.length == 0) {
        buyerAccountIdentifier = 'accountNumber:' + payable.accountInfo.number;
      }

      const pay = {
        accountInfo: payable.accountInfo,
      } as UpdateSessionPayable;
      // eslint-disable-next-line prefer-const
      let documentList: UpdatePayableDocument[] = [];
      payable.documents.forEach((document) => {
        const doc = {
          urn: document.burn,
          invoiceNumber: document.invoiceNumber,
          poNumber: document.poNumber,
          documentDate: document.documentDate,
          dueDate: document.dueDate,
          originalBalance: document.originalBalance,
          openBalance: document.openBalance,
          payableAmount: document.payableAmount,
          currencyCode: document.currencyCode,
          amountPaid: document.amountPaid,
          documentType: document.documentType,
        } as UpdatePayableDocument;
        documentList.push(doc);
      });
      pay.documents = documentList;
      updateSessionPayable.push(pay);
    });
    return {
      buyerAccountIdentifier: buyerAccountIdentifier,
      payables: updateSessionPayable,
    } as UpdatePayableSessionRequestV1;
  };
}
